import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Input,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';

export interface FilterConfig {
  id: string;
  label: string;
  width: number;
}

@Component({
  selector: 'so-filter-bar-ui',
  standalone: true,
  imports: [CommonModule, MatIconModule, SoButtonGradientUI],
  templateUrl: './so-filter-bar-responsive.component.html',
  styleUrl: './so-filter-bar-responsive.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoFilterBarUI implements AfterViewInit {
  private viewInitialized = signal<boolean>(false);

  protected readonly filtersSig = signal<FilterConfig[]>([]);
  protected readonly showClearButtonSig = signal<boolean>(true);
  protected readonly clearButtonTextSig = signal<string>('Limpiar');

  clearFilters = output<void>();
  openFilter = output<string>();

  @ContentChild('filtersTemplate') filtersTemplate!: TemplateRef<any>;
  @ViewChild('filtersContainer') filtersContainer!: ElementRef<HTMLElement>;

  private isMoreFiltersOpenSig = signal<boolean>(false);
  private visibleFiltersSig = signal<string[]>([]);
  private overflowFiltersSig = signal<string[]>([]);

  private hasOverflowFiltersSig = computed(() => this.overflowFiltersSig().length > 0);

  constructor() {
    effect(() => {
      this.filtersSig();
      if (!this.viewInitialized()) return;

      this.calculateVisibleFilters();
    }, {  });
  }

  @Input()
  set filters(value: FilterConfig[]) {
    this.filtersSig.set(value ?? []);
  }

  get filters(): FilterConfig[] {
    return this.filtersSig();
  }

  @Input()
  set showClearButton(value: boolean) {
    this.showClearButtonSig.set(value ?? true);
  }

  get showClearButton(): boolean {
    return this.showClearButtonSig();
  }

  @Input()
  set clearButtonText(value: string) {
    this.clearButtonTextSig.set(value ?? 'Limpiar');
  }

  get clearButtonText(): string {
    return this.clearButtonTextSig();
  }

  get isMoreFiltersOpen(): boolean {
    return this.isMoreFiltersOpenSig();
  }

  get visibleFilters(): string[] {
    return this.visibleFiltersSig();
  }

  get overflowFilters(): string[] {
    return this.overflowFiltersSig();
  }

  get hasOverflowFilters(): boolean {
    return this.hasOverflowFiltersSig();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.calculateVisibleFilters();
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    setTimeout(() => this.calculateVisibleFilters(), 100);
  }

  private calculateVisibleFilters(): void {
    if (!this.filtersContainer?.nativeElement || !this.filters.length) return;

    const container = this.filtersContainer.nativeElement;
    const containerWidth = container.offsetWidth;

    // Espacio reservado para botón "Ver más" (~130px) y "Limpiar" (~100px) y gaps (~40px)
    const reservedSpace = 270;
    const availableWidth = containerWidth - reservedSpace;

    let usedWidth = 0;
    const visible: string[] = [];
    const overflow: string[] = [];

    // Usar anchos estimados para calcular qué filtros caben
    this.filters.forEach((filter) => {
      const pillWidth = filter.width + 8; // 8px gap

      if (usedWidth + pillWidth <= availableWidth) {
        usedWidth += pillWidth;
        visible.push(filter.id);
      } else {
        overflow.push(filter.id);
      }
    });

    this.visibleFiltersSig.set(visible);
    this.overflowFiltersSig.set(overflow);

    if (!overflow.length) {
      this.isMoreFiltersOpenSig.set(false);
    }
  }

  isFilterVisible(filterId: string): boolean {
    return this.visibleFilters.includes(filterId) || this.visibleFilters.length === 0;
  }

  isFilterInOverflow(filterId: string): boolean {
    return this.overflowFilters.includes(filterId);
  }

  toggleMoreFilters(): void {
    this.isMoreFiltersOpenSig.update(v => !v);
  }

  closeMoreFilters(): void {
    this.isMoreFiltersOpenSig.set(false);
  }

  openFilterFromDropdown(filterId: string): void {
    this.isMoreFiltersOpenSig.set(false);
    this.openFilter.emit(filterId);
  }

  onClearFilters(): void {
    this.clearFilters.emit();
  }

  getFilterLabel(filterId: string): string {
    const filter = this.filters.find(f => f.id === filterId);
    return filter?.label || filterId;
  }
}
